import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle,
  MessageSquareHeart,
  XCircle,
  Star,
  Bug,
  Sparkles,
  Palette,
  BookOpen,
} from 'lucide-react';
import { Button } from './UIComponents';
import { supabase } from '../services/supabaseClient';
import logger from '../services/logger';

export const BetaFeedbackModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  userEmail: string;
}> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [category, setCategory] = useState('other');
  const [rating, setRating] = useState(0);
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  if (!isOpen) return null;

  const handleSend = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        logger.error('BetaFeedbackModal', 'Feedback error: no session');
        setSending(false);
        return;
      }

      const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:3000' : '';
      const res = await fetch(`${apiBase}/api/feedback/beta`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          category,
          rating: rating > 0 ? rating : null,
          message: message.trim(),
          page: 'profile',
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        logger.error('BetaFeedbackModal', 'Feedback error:', data.error);
        setSending(false);
        return;
      }
    } catch (e) {
      logger.error('BetaFeedbackModal', 'Feedback error:', e);
      setSending(false);
      return;
    }

    // Trigger support confirmation email (fire-and-forget)
    try {
      const {
        data: { session: s },
      } = await supabase.auth.getSession();
      if (s) {
        const apiBase =
          window.location.hostname === 'localhost'
            ? 'http://localhost:3000'
            : 'https://app.fahryo.de';
        fetch(`${apiBase}/api/email/trigger/support`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${s.access_token}`,
          },
          body: JSON.stringify({ subject: message.trim().substring(0, 60), category }),
        }).catch(() => {});
      }
    } catch (err) {
      logger.error('BetaFeedbackModal', 'Support email trigger failed:', err);
    }

    setSending(false);
    setSent(true);
    setTimeout(() => {
      setSent(false);
      setMessage('');
      setRating(0);
      setCategory('other');
      onClose();
    }, 2000);
  };

  const categories = [
    { id: 'bug', label: t('feedback_bug'), Icon: Bug },
    { id: 'feature', label: t('feedback_feature'), Icon: Sparkles },
    { id: 'ui', label: 'Design', Icon: Palette },
    { id: 'content', label: 'Inhalt', Icon: BookOpen },
    { id: 'other', label: t('feedback_other'), Icon: MessageSquareHeart },
  ];

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-labelledby="feedback-modal-title"
    >
      <div className="glass-strong max-w-sm w-full p-6">
        {sent ? (
          <div className="text-center py-8">
            <CheckCircle size={48} className="mx-auto text-green-500 mb-4 animate-bounce" />
            <h3 className="font-bold text-xl mb-2 text-[var(--f-text)]">{t('Vielen Dank!')}</h3>
            <p className="text-gray-500 text-sm">
              {t('Dein Feedback hilft uns, Fahryo besser zu machen.')}
            </p>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-lg text-[var(--f-text)] flex items-center gap-2">
                <MessageSquareHeart size={18} className="text-purple-500" />
                {t('Feedback')}
              </h3>
              <button onClick={onClose}>
                <XCircle size={20} className="text-gray-400" />
              </button>
            </div>

            {/* Star Rating */}
            <div className="mb-4">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 font-medium">
                {t('Wie gefällt dir Fahryo?')}
              </p>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <button
                    key={i}
                    onClick={() => setRating(i)}
                    className="transition-transform hover:scale-110 active:scale-95"
                  >
                    <Star
                      size={28}
                      style={{
                        fill: i <= rating ? '#f59e0b' : 'transparent',
                        color: i <= rating ? '#f59e0b' : '#d1d5db',
                        transition: 'all 0.15s ease',
                      }}
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Category */}
            <div className="mb-4">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 font-medium">
                {t('Kategorie')}
              </p>
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setCategory(cat.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all glass-pill ${
                      category === cat.id
                        ? 'text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-600'
                        : 'text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    <cat.Icon size={11} />
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Message */}
            <textarea
              className="glass-input w-full h-28 p-3 rounded-xl text-sm outline-none focus:border-purple-500 mb-4 text-[var(--f-text)] resize-none"
              placeholder={t('Was können wir verbessern? Was fehlt dir? Was gefällt dir gut?')}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />

            <Button fullWidth onClick={handleSend} disabled={!message.trim() || sending}>
              {sending ? t('Wird gesendet...') : t('Feedback senden')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default BetaFeedbackModal;
